import React, { useState, useEffect } from 'react';
import { Alert, Form, Table, Button, Card, FilterGrid } from '@/components/ui';
import ReportParametersCard from '../components/ReportParametersCard';
import { areasService, ServiceArea } from '../../../services/areasService';
import reportService from '../../../services/reportService';
import InfoTooltip from '../../../components/InfoTooltip';
import Toast from '../../../components/Toast';
import { formatDollars } from '../../../utils/formatDollars';

interface QBSSummaryReportViewProps {
  serviceAreas: ServiceArea[];
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

interface TableRow {
  id: string;
  label: string;
  isParent: boolean;
  parentId?: string;
  values: (number | string)[];
  highlightColor?: string;
}

const QBSSummaryReportView: React.FC<QBSSummaryReportViewProps> = ({
  serviceAreas,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    reportingMonth: '', // Placeholder, disabled for now
    quantifiedYear: '',
    startDate: '',
    endDate: ''
  });
  const [qbsPlanYearOptions, setQbsPlanYearOptions] = useState<number[]>([]);
  const [loadingQbsPlanYears, setLoadingQbsPlanYears] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [monthHeaders, setMonthHeaders] = useState<Array<{ label: string; dateRange: string }>>([]);
  const [annualTarget, setAnnualTarget] = useState<number | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);

  const currentYear = new Date().getFullYear();

  // Update service area filter when defaultServiceAreaId changes
  useEffect(() => {
    if (defaultServiceAreaId && !filters.serviceArea) {
      setFilters(prev => ({ ...prev, serviceArea: defaultServiceAreaId }));
    }
  }, [defaultServiceAreaId]);

  useEffect(() => {
    if (!filters.serviceArea) {
      setQbsPlanYearOptions([]);
      setFilters((prev) => ({ ...prev, quantifiedYear: '' }));
      return;
    }
    let cancelled = false;
    const serviceAreaId = parseInt(filters.serviceArea, 10);
    if (Number.isNaN(serviceAreaId)) {
      setQbsPlanYearOptions([]);
      return;
    }
    setLoadingQbsPlanYears(true);
    areasService
      .getQuantifiedPlans(serviceAreaId)
      .then((plans) => {
        if (cancelled) return;
        const eligible = plans.filter(
          (p) => p.status === 'Open' || p.status === 'Closed'
        );
        const years = Array.from(new Set(eligible.map((p) => p.year))).sort(
          (a, b) => b - a
        );
        setQbsPlanYearOptions(years);
        const activePlan = eligible.find((p) => p.isActive);
        const openYearValues = eligible
          .filter((p) => p.status === 'Open')
          .map((p) => p.year);
        let defaultYear: number | null = null;

        if (years.includes(currentYear)) {
          defaultYear = currentYear;
        } else if (activePlan && years.includes(activePlan.year)) {
          defaultYear = activePlan.year;
        } else if (openYearValues.length > 0) {
          defaultYear = Math.max(...openYearValues);
        } else if (years.length > 0) {
          defaultYear = Math.max(...years);
        }

        setFilters((prev) => ({
          ...prev,
          quantifiedYear:
            defaultYear != null ? String(defaultYear) : ''
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setQbsPlanYearOptions([]);
          setFilters((prev) => ({ ...prev, quantifiedYear: '' }));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingQbsPlanYears(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.serviceArea]);

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleExport = async () => {
    if (!filters.serviceArea || !filters.quantifiedYear) {
      setErrorMessage('Please select Service Area and Quantified Year');
      return;
    }

    setLoadingExport(true);
    setErrorMessage(null);
    try {
      await reportService.downloadQBSSummary({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });
      showSuccessToast('QBS Summary report exported successfully!');
    } catch (error: any) {
      console.error('Error exporting QBS Summary report:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to export QBS Summary report. Please try again later.');
    } finally {
      setLoadingExport(false);
    }
  };

  const handleGeneratePreview = async () => {
    if (!filters.serviceArea || !filters.quantifiedYear) {
      setErrorMessage('Please select Service Area and Quantified Year');
      return;
    }

    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const data = await reportService.getQBSSummary({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });
      setReportData(data);
      setMonthHeaders(data.monthHeaders || []);
      setAnnualTarget(data.annualTarget || null);
    } catch (error: any) {
      console.error('Error generating QBS Summary report:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to generate QBS Summary report. Please try again later.');
      setReportData(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Build table rows from report data
  const buildTableRows = (): TableRow[] => {
    const emptyValues = Array(13).fill('');

    const getTargetValue = () => {
      if (annualTarget !== null && annualTarget !== undefined) {
        return annualTarget;
      }
      if (reportData?.totals?.expectedTotal) {
        return reportData.totals.expectedTotal;
      }
      return 0;
    };

    if (!reportData) {
      // Return empty structure if no data
      return [
        // Actioned summary section (highlighted)
        { id: 'actioned-summary', label: 'Actioned Summary', isParent: true, values: emptyValues, highlightColor: '#fff3cd' },
        { id: 'actioned-summary-subtotal', label: 'Monthly Actioned Subtotal', isParent: false, parentId: 'actioned-summary', values: emptyValues, highlightColor: '#fffaf0' },
        { id: 'actioned-summary-percent', label: 'Actioned Subtotal %', isParent: false, parentId: 'actioned-summary', values: emptyValues, highlightColor: '#fffaf0' },
        { id: 'actioned-summary-rolling', label: 'Rolling Actioned Subtotal', isParent: false, parentId: 'actioned-summary', values: emptyValues, highlightColor: '#fffaf0' },
        { id: 'actioned-summary-rolling-percent', label: 'Rolling Actioned Subtotal %', isParent: false, parentId: 'actioned-summary', values: emptyValues, highlightColor: '#fffaf0' },

        { id: 'reported', label: 'Reported', isParent: true, values: emptyValues },
        { id: 'reported-finalised', label: 'Finalized', isParent: false, parentId: 'reported', values: emptyValues },
        { id: 'reported-percent', label: '% Reported', isParent: false, parentId: 'reported', values: emptyValues },
        { id: 'complete', label: 'Complete', isParent: true, values: emptyValues },
        { id: 'complete-closed', label: 'Closed', isParent: false, parentId: 'complete', values: emptyValues },
        { id: 'complete-internal-review', label: 'Internal Review', isParent: false, parentId: 'complete', values: emptyValues },
        { id: 'complete-subtotal', label: 'Sub total for work reporting', isParent: false, parentId: 'complete', values: emptyValues },
        { id: 'complete-percent', label: '% of work Reporting', isParent: false, parentId: 'complete', values: emptyValues },
        { id: 'active', label: 'Active', isParent: true, values: emptyValues },
        { id: 'active-marked-complete', label: 'Active Marked as complete', isParent: false, parentId: 'active', values: emptyValues },
        { id: 'active-active', label: 'Active**', isParent: false, parentId: 'active', values: emptyValues },
        { id: 'active-subtotal', label: 'Sub Total', isParent: false, parentId: 'active', values: emptyValues },
        { id: 'the-plan', label: 'The Plan', isParent: true, values: emptyValues },
        { id: 'the-plan-planned', label: 'Planned', isParent: false, parentId: 'the-plan', values: emptyValues },
        // Removed: the-plan-subtotal row
        { id: 'the-plan-total', label: 'Total Plan', isParent: false, parentId: 'the-plan', values: emptyValues },
        { id: 'the-plan-estimated-percent', label: 'Estimated % of Plan', isParent: false, parentId: 'the-plan', values: emptyValues },
        { id: 'the-plan-remaining', label: 'Value of Plan Remaining', isParent: false, parentId: 'the-plan', values: emptyValues },
        { id: 'holding', label: 'Holding', isParent: true, values: emptyValues },
        { id: 'holding-proposed', label: 'Proposed', isParent: false, parentId: 'holding', values: emptyValues },
        { id: 'profitability', label: 'Profitability', isParent: true, values: emptyValues },
        { id: 'profitability-value-earned', label: 'Total Value Earned', isParent: false, parentId: 'profitability', values: emptyValues },
        { id: 'profitability-costs', label: 'Total Costs', isParent: false, parentId: 'profitability', values: emptyValues },
        { id: 'profitability-margin', label: 'Gross Margin', isParent: false, parentId: 'profitability', values: emptyValues },
        { id: 'profitability-percent', label: 'GM%', isParent: false, parentId: 'profitability', values: emptyValues },
      ];
    }

    const { sections } = reportData;
    const targetValue = getTargetValue();

    const actionedValues: number[] = sections.active.actioned?.values || Array(13).fill(0);

    const buildPercentValues = (sourceValues: number[]): (number | string)[] => {
      if (!targetValue) {
        return Array(13).fill('');
      }
      return sourceValues.map(value => (value / targetValue) * 100);
    };

    const rollingActionedValues = (() => {
      const monthly = actionedValues.slice(0, 12);
      const rolling: number[] = [];
      monthly.forEach((value: number, index: number) => {
        const previous = index === 0 ? 0 : rolling[index - 1];
        rolling.push(previous + value);
      });
      const totalToDate = rolling.length ? rolling[rolling.length - 1] : 0;
      rolling.push(totalToDate); // Total column
      return rolling;
    })();

    const actionedPercentValues = buildPercentValues(actionedValues);
    const rollingActionedPercentValues = buildPercentValues(rollingActionedValues);
    
    return [
      // Actioned summary section (highlighted and pinned to top)
      { id: 'actioned-summary', label: 'Actioned Summary', isParent: true, values: Array(13).fill(''), highlightColor: '#fff3cd' },
      { id: 'actioned-summary-subtotal', label: 'Monthly Actioned Subtotal', isParent: false, parentId: 'actioned-summary', values: actionedValues, highlightColor: '#fffaf0' },
      { id: 'actioned-summary-percent', label: 'Actioned Subtotal %', isParent: false, parentId: 'actioned-summary', values: actionedPercentValues, highlightColor: '#fffaf0' },
      { id: 'actioned-summary-rolling', label: 'Rolling Actioned Subtotal', isParent: false, parentId: 'actioned-summary', values: rollingActionedValues, highlightColor: '#fffaf0' },
      { id: 'actioned-summary-rolling-percent', label: 'Rolling Actioned Subtotal %', isParent: false, parentId: 'actioned-summary', values: rollingActionedPercentValues, highlightColor: '#fffaf0' },

      // Reported section
      { id: 'reported', label: 'Reported', isParent: true, values: Array(13).fill('') },
      { id: 'reported-finalised', label: 'Finalized', isParent: false, parentId: 'reported', values: sections.reported.finalized.values },
      { id: 'reported-percent', label: '% Reported', isParent: false, parentId: 'reported', values: sections.reported.percent.values },
      
      // Complete section
      { id: 'complete', label: 'Complete', isParent: true, values: Array(13).fill('') },
      { id: 'complete-closed', label: 'Closed', isParent: false, parentId: 'complete', values: sections.complete.closed.values },
      { id: 'complete-internal-review', label: 'Internal Review', isParent: false, parentId: 'complete', values: sections.complete.internalReview.values },
      { id: 'complete-subtotal', label: 'Sub total for work reporting', isParent: false, parentId: 'complete', values: sections.complete.subtotal.values },
      { id: 'complete-percent', label: '% of work Reporting', isParent: false, parentId: 'complete', values: sections.complete.percent.values },
      
      // Active section
      { id: 'active', label: 'Active', isParent: true, values: Array(13).fill('') },
      { id: 'active-marked-complete', label: 'Active Marked as complete', isParent: false, parentId: 'active', values: sections.active.markedComplete.values },
      { id: 'active-active', label: 'Active**', isParent: false, parentId: 'active', values: sections.active.active.values },
      { id: 'active-subtotal', label: 'Sub Total', isParent: false, parentId: 'active', values: sections.active.subtotal.values },
      
      // The Plan section
      { id: 'the-plan', label: 'The Plan', isParent: true, values: Array(13).fill('') },
      { id: 'the-plan-planned', label: 'Planned', isParent: false, parentId: 'the-plan', values: sections.thePlan?.planned?.values || Array(13).fill('') },
      // Removed: the-plan-subtotal row
      { id: 'the-plan-total', label: 'Total Plan', isParent: false, parentId: 'the-plan', values: sections.thePlan?.total?.values || Array(13).fill('') },
      { id: 'the-plan-estimated-percent', label: 'Estimated % of Plan', isParent: false, parentId: 'the-plan', values: sections.thePlan?.estimatedPercent?.values || Array(13).fill('') },
      { id: 'the-plan-remaining', label: 'Value of Plan Remaining', isParent: false, parentId: 'the-plan', values: sections.thePlan?.remaining?.values || Array(13).fill('') },
      
      // Holding section
      { id: 'holding', label: 'Holding', isParent: true, values: Array(13).fill('') },
      { id: 'holding-proposed', label: 'Proposed', isParent: false, parentId: 'holding', values: sections.holding?.proposed?.values || Array(13).fill('') },
      
      // Profitability section
      { id: 'profitability', label: 'Profitability', isParent: true, values: Array(13).fill('') },
      { id: 'profitability-value-earned', label: 'Total Value Earned', isParent: false, parentId: 'profitability', values: sections.profitability.totalValueEarned.values },
      { id: 'profitability-costs', label: 'Total Costs', isParent: false, parentId: 'profitability', values: sections.profitability?.totalCosts?.values || Array(13).fill('') },
        { id: 'profitability-margin', label: 'Gross Margin', isParent: false, parentId: 'profitability', values: sections.profitability?.profitMargin?.values || Array(13).fill('') },
        { id: 'profitability-percent', label: 'GM%', isParent: false, parentId: 'profitability', values: sections.profitability?.profitPercent?.values || Array(13).fill('') },
    ];
  };

  const tableRows = buildTableRows();

  // Tooltip explanations for each metric
  const getTooltipContent = (rowId: string): string => {
    const tooltips: Record<string, string> = {
      'actioned-summary': 'High-level rollup of all actioned work (reported + complete + active).',
      'actioned-summary-subtotal': 'Monthly Actioned Subtotal (reported + complete + active) per month.',
      'actioned-summary-percent': 'Monthly Actioned Subtotal shown as a percentage of the annual target (or expected total when no target is set).',
      'actioned-summary-rolling': 'Cumulative Actioned Subtotal that adds each month on top of the previous months.',
      'actioned-summary-rolling-percent': 'Rolling Actioned Subtotal expressed as a percentage of the annual target (or expected total when no target is set).',
      'reported': 'Section containing tasks that have been finalized and reported.',
      'reported-finalised': 'Sum of actual total value for tasks with FINALIZED status, grouped by the month of their finalized timestamp.',
      'reported-percent': 'Percentage of Finalized value compared to the annual target (annualTarget + annualAdjustment).',
      'complete': 'Section containing tasks that are in the completion workflow.',
      'complete-closed': 'Sum of actual total value for tasks with CLOSED status, grouped by the month of their closed timestamp.',
      'complete-internal-review': 'Sum of actual total value for tasks with INTERNAL_REVIEW status, grouped by the month of their internal review timestamp.',
      'complete-subtotal': 'Sum of Closed and Internal Review values.',
      'complete-percent': 'Percentage of Complete subtotal compared to the total expected value across all tasks.',
      'active': 'Section containing tasks that are currently active.',
      'active-marked-complete': 'Sum of actual total value for tasks with ACTIVE_MARKED_AS_COMPLETE status, grouped by the month of their completion timestamp (uses completedTimestamp, completionDate, or task history timestamp).',
      'active-active': 'Sum of actual total value for tasks with ACTIVE status, grouped by the month of their active timestamp.',
      'active-subtotal': 'Sum of Active Marked as Complete and Active values.',
      'the-plan': 'Section containing planned tasks that are scheduled to be completed.',
      'the-plan-planned': 'Sum of expected total value (totalValue) for tasks with PLANNED status, grouped by the month of their planned completion date (planned end date).',
      'the-plan-total': 'Total of all Planned values across all months.',
      'the-plan-estimated-percent': 'Estimated percentage of plan completion (currently not calculated).',
      'the-plan-remaining': 'Value of plan remaining to be completed (currently not calculated).',
      'holding': 'Section containing proposed tasks that are on hold.',
      'holding-proposed': 'Sum of expected total value (totalValue) for tasks with PROPOSED status, grouped by planned completion date, then planned start date, and if both are empty allocated to the current month.',
      'profitability': 'Section showing profitability metrics based on completed work.',
      'profitability-value-earned': 'Sum of actual total value for tasks with FINALIZED and CLOSED statuses, grouped by the month of their respective timestamps.',
      'profitability-costs': 'Sum of all effort costs (quantity × rate) for tasks with FINALIZED and CLOSED statuses, grouped by the month of each effort date.',
      'profitability-margin': 'Difference between Total Value Earned and Total Costs for each month (Value Earned - Costs).',
      'profitability-percent': 'Gross Margin expressed as a percentage of Total Value Earned for each month ((Gross Margin / Value Earned) × 100).'
    };
    return tooltips[rowId] || '';
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Header Section - Fixed */}
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        
        <ReportParametersCard
          onGenerate={handleGeneratePreview}
          onExport={() => {}}
          loadingGenerate={loadingPreview}
          loadingExport={false}
          disableExport={true}
          generateButtonText="Run Report"
          hideExportButton={true}
          defaultCollapsed={false}
          onCollapseChange={setIsFiltersCollapsed}
        >
          <FilterGrid>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
              <Form.Select
                size="sm"
                value={filters.serviceArea}
                onChange={(e) => setFilters(prev => ({ ...prev, serviceArea: e.target.value }))}
                disabled={loadingServiceAreas}
              >
                <option value="">All Service Areas</option>
                {serviceAreas.map((area) => (
                  <option key={area.id} value={area.id.toString()}>
                    {area.name || area.code || 'Unknown'}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Start Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">End Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Quantified Year</Form.Label>
              <Form.Select
                size="sm"
                value={filters.quantifiedYear}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    quantifiedYear: e.target.value
                  }))
                }
                disabled={
                  !filters.serviceArea ||
                  loadingQbsPlanYears ||
                  qbsPlanYearOptions.length === 0
                }
              >
                <option value="">
                  {!filters.serviceArea
                    ? 'Select service area first'
                    : loadingQbsPlanYears
                      ? 'Loading years…'
                      : qbsPlanYearOptions.length === 0
                        ? 'No quantified plans'
                        : 'Select year'}
                </option>
                {qbsPlanYearOptions.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </FilterGrid>
        </ReportParametersCard>
      </div>
      
      {/* Report Data Section - fills the remaining pane height */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0
      }}>
        <Card style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0
        }}>
          <Card.Header style={{ flexShrink: 0 }}>
            <div className="tw-flex tw-justify-between tw-items-center">
              <h6 className="tw-mb-0">Accomplishment Reporting</h6>
              <div className="tw-flex tw-items-center tw-gap-3">
                {annualTarget !== null && (
                  <div className="tw-text-content-muted" style={{ fontSize: '0.9rem' }}>
                    <strong>Annual Target:</strong>{' '}
                    {formatDollars(Math.round(annualTarget), {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </div>
                )}
                {reportData && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleExport}
                    disabled={!filters.serviceArea || !filters.quantifiedYear || loadingExport}
                  >
                    <i className="bi bi-download tw-mr-1"></i>
                    {loadingExport ? 'Exporting...' : 'Export Report'}
                  </Button>
                )}
              </div>
            </div>
          </Card.Header>

          {/* Table Container - a single scroll region that fills the Card, sized purely by flex
              (no viewport-relative maxHeight - that was the "horizontal scroll only appears at
              the bottom of the page" bug, since calc(100vh - 350px) doesn't know how tall the
              sidebar/filters actually left this pane on a given screen). */}
          <Card.Body
            className="tw-p-0"
            style={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              overflowX: 'auto',
              overflowY: 'auto'
            }}
          >
              <Table
                striped
                bordered
                hover
                style={{ marginBottom: 0, width: 'max-content', minWidth: '100%' }}
              >
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
                  <tr>
                    <th
                      style={{
                        minWidth: '250px',
                        backgroundColor: '#f8f9fa',
                        position: 'sticky',
                        left: 0,
                        zIndex: 12,
                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                      }}
                      title="Metric"
                    >Metric</th>
                    {monthHeaders.length > 0 ? (
                      monthHeaders.map((month) => (
                        <th
                          key={month.label}
                          style={{ minWidth: '100px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}
                          title={month.dateRange ? `${month.label} — ${month.dateRange}` : month.label}
                        >
                          <div>{month.label}</div>
                          {month.dateRange && (
                            <div style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#6c757d' }}>
                              {month.dateRange}
                            </div>
                          )}
                        </th>
                      ))
                    ) : (
                      Array.from({ length: 12 }, (_, i) => (
                        <th key={`M${i + 1}`} style={{ minWidth: '100px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title={`M${i + 1}`}>
                          {`M${i + 1}`}
                        </th>
                      ))
                    )}
                    <th style={{ minWidth: '120px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title="Total to date">
                      Total to date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row) => (
                    <tr key={row.id}>
                      <td
                        style={{
                          fontWeight: row.isParent ? 'bold' : 'normal',
                          paddingLeft: row.isParent ? '12px' : '36px',
                          backgroundColor: row.highlightColor || (row.isParent ? '#e9ecef' : 'white'),
                          position: 'sticky',
                          left: 0,
                          zIndex: 9,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        <InfoTooltip
                          content={getTooltipContent(row.id)}
                          id={`tooltip-${row.id}`}
                          placement="right"
                          maxWidth="500px"
                        >
                          {row.label}
                        </InfoTooltip>
                      </td>
                      {row.values.map((value, index) => {
                        // Check if this is a percentage row - check both id and label
                        const isPercentageRow = row.id.includes('percent') || 
                                                row.label.includes('%') || 
                                                row.label.toLowerCase().includes('percent');
                        
                        // Check if this is the Profit % row for conditional formatting
                        const isProfitPercentRow = row.id === 'profitability-percent';
                        
                        // Format the value
                        let displayValue: string | number = value;
                        if (value === '' || value === null || value === undefined) {
                          // For parent rows (header rows), show empty string instead of "-"
                          displayValue = row.isParent ? '' : '-';
                        } else if (typeof value === 'number') {
                          if (isPercentageRow) {
                            // For percentage rows, always show as percentage with 2 decimals
                            displayValue = `${value.toFixed(2)}%`;
                          } else {
                            // For currency rows, format as currency rounded to nearest dollar
                            if (value === 0) {
                              displayValue = '-';
                            } else {
                              displayValue = formatDollars(Math.round(value), {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              });
                            }
                          }
                        }
                        
                        // Determine color for Profit % column
                        let cellColor: string | undefined;
                        if (isProfitPercentRow && typeof value === 'number') {
                          if (value < 0) {
                            cellColor = '#dc3545'; // red for negative
                          } else if (value > 0) {
                            cellColor = '#28a745'; // green for positive
                          }
                        }
                        
                        return (
                          <td
                            key={index}
                            style={{
                              textAlign: 'right',
                              padding: '8px 12px',
                              color: cellColor,
                              backgroundColor: row.isParent ? '#e9ecef' : row.highlightColor,
                              fontWeight: row.isParent ? 'bold' : 'normal'
                            }}
                          >
                            {displayValue}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Toast Notification */}
          <Toast
            message={toastMessage}
            type="success"
            show={showToast}
            onClose={() => setShowToast(false)}
          />
        </div>
      </div>
  );
};

export default QBSSummaryReportView;

